import { useState } from 'react'
import { AlertTriangle, Pencil, Plus, ScrollText } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, PageHeader, PermissionGate, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { Select } from '@/components/ui/select'
import {
  useAppraisalTemplates, useCompetencies, usePerformanceConfigMutations, useRatingScales,
} from '../api'

/**
 * Rating scales, competencies and templates.
 *
 * Everything here is empty on a fresh install, and each empty state says why rather than
 * looking unfinished. A shipped rating scale would decide what "meets expectations" means for
 * every appraisal ever run in this system.
 */
export function PerformanceSetupPage() {
  useBreadcrumb()
  const scales = useRatingScales()
  const competencies = useCompetencies()
  const templates = useAppraisalTemplates()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const {
    saveScale, addLevel, setGapThreshold, saveCompetency, saveTemplate, addSection,
    retireScale, retireCompetency, retireTemplate,
  } = usePerformanceConfigMutations()

  const [scaleDraft, setScaleDraft] = useState<{
    id?: string; code: string; name: string
  } | null>(null)
  const [levelDraft, setLevelDraft] = useState<{
    scaleId: string; levelValue: string; label: string; score: string
  } | null>(null)
  const [gapDraft, setGapDraft] = useState<{ scaleId: string; gapBelowLevel: string } | null>(null)
  const [competencyDraft, setCompetencyDraft] = useState<{
    id?: string; code: string; name: string; category: string
  } | null>(null)
  const [templateDraft, setTemplateDraft] = useState<{
    id?: string
    scaleId: string; code: string; name: string; includeSelf: boolean; includeManager: boolean
  } | null>(null)
  const [sectionDraft, setSectionDraft] = useState<{
    templateId: string; sectionOrder: string; name: string; weightPercent: string
    competencyId: string
  } | null>(null)
  const [retiring, setRetiring] = useState<
    { kind: 'scale' | 'competency' | 'template'; id: string; name: string } | null
  >(null)
  const [error, setError] = useState<string | null>(null)

  const run = (work: () => Promise<unknown>, close: () => void, fallback: string) => {
    void (async () => {
      setError(null)
      try {
        await work()
        close()
      } catch (caught) {
        setError(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Performance setup"
        subtitle="How this organisation scores, what it assesses, and on what form."
      />

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <ScrollText className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Nothing here ships with the product. A rating scale decides what "meets expectations"
        means for every appraisal run in this system, and competencies often come from a
        framework an employer has licensed. Both have to be yours.
      </p>

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <PageHeader
        title="Rating scales"
        subtitle="The points somebody can be rated at, and what each is worth."
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() => { setError(null); setScaleDraft({ code: '', name: '' }) }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New scale
          </Button>
        }
      />
      <Card>
        {scales.isPending && <Skeleton className="m-4 h-20" />}
        {scales.isError && <ErrorState error={scales.error} onRetry={() => void scales.refetch()} />}
        {scales.isSuccess && scales.data.length === 0 && (
          <EmptyState
            title="No rating scale is defined"
            description="Nothing can be appraised until one exists. Three points, five, nine-box, letters — this is the most organisation-specific choice in the module."
          />
        )}
        {scales.isSuccess && scales.data.map((scale) => (
          <div key={scale.id} className="border-b border-slate-100 p-3 last:border-b-0">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium">
                  {scale.name} <span className="font-mono text-xs text-slate-600">{scale.code}</span>
                </p>
                <p className="mt-1 text-xs text-slate-600">
                  {scale.levels.length === 0
                    ? 'No levels yet — nothing can be rated against it.'
                    : scale.levels
                        .map((level) => `${level.levelValue}. ${level.label} (${level.score})`)
                        .join(' · ')}
                </p>
                <p className="mt-1 text-xs">
                  {scale.gapBelowLevel == null ? (
                    <span className="text-amber-700">
                      No gap threshold. This scale never creates a training need — which is a
                      choice, not an omission.
                    </span>
                  ) : (
                    <span className="text-slate-600">
                      Ratings below level {scale.gapBelowLevel} become training needs.
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setError(null)
                    setLevelDraft({
                      scaleId: scale.id,
                      levelValue: String(scale.levels.length + 1),
                      label: '', score: '',
                    })
                  }}
                >
                  Add level
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setError(null)
                    setGapDraft({
                      scaleId: scale.id,
                      gapBelowLevel: scale.gapBelowLevel == null
                        ? '' : String(scale.gapBelowLevel),
                    })
                  }}
                >
                  Gap threshold
                </Button>
                <PermissionGate permission="performance:configure">
                  <Button
                    type="button" variant="ghost"
                    aria-label={`Edit ${scale.name}`}
                    onClick={() => {
                      setError(null)
                      setScaleDraft({ id: scale.id, code: scale.code, name: scale.name })
                    }}
                  >
                    <Pencil className="size-4" aria-hidden="true" />
                    Edit
                  </Button>
                  {scale.active && (
                    <Button
                      type="button" variant="outline"
                      onClick={() => {
                        setError(null)
                        setRetiring({ kind: 'scale', id: scale.id, name: scale.name })
                      }}
                    >
                      Retire
                    </Button>
                  )}
                </PermissionGate>
              </div>
            </div>
          </div>
        ))}
      </Card>

      <PageHeader
        title="Competencies"
        subtitle="What is assessed."
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setError(null)
              setCompetencyDraft({ code: '', name: '', category: '' })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New competency
          </Button>
        }
      />
      <Card>
        {competencies.isSuccess && competencies.data.length === 0 && (
          <EmptyState
            title="No competency is defined"
            description="None ships with the product — many employers license a framework and are not free to use somebody else's."
          />
        )}
        {competencies.isSuccess && competencies.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Code</th>
                  <th className="p-3 text-start font-medium">Name</th>
                  <th className="p-3 text-start font-medium">Category</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {competencies.data.map((competency) => (
                  <tr key={competency.id} className="border-b border-slate-100">
                    <td className="p-3 font-mono">{competency.code}</td>
                    <td className="p-3">{competency.name}</td>
                    <td className="p-3 text-slate-600">{competency.category ?? '—'}</td>
                    <td className="p-3 text-end">
                      <PermissionGate permission="performance:configure">
                        <Button
                          type="button" variant="ghost" size="sm"
                          aria-label={`Edit ${competency.name}`}
                          onClick={() => {
                            setError(null)
                            setCompetencyDraft({
                              id: competency.id,
                              code: competency.code,
                              name: competency.name,
                              category: competency.category ?? '',
                            })
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {competency.active && (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => {
                              setError(null)
                              setRetiring({
                                kind: 'competency', id: competency.id, name: competency.name,
                              })
                            }}
                          >
                            Retire
                          </Button>
                        )}
                      </PermissionGate>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <PageHeader
        title="Appraisal templates"
        subtitle="Weighted sections. They must total 100% before a cycle can use them."
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setError(null)
              setTemplateDraft({
                scaleId: scales.data?.[0]?.id ?? '', code: '', name: '',
                includeSelf: true, includeManager: true,
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New template
          </Button>
        }
      />
      <Card>
        {templates.isSuccess && templates.data.length === 0 && (
          <EmptyState
            title="No template is defined"
            description="A template is the form people are appraised on: weighted sections over your competencies."
          />
        )}
        {templates.isSuccess && templates.data.map((template) => (
          <div key={template.id} className="border-b border-slate-100 p-3 last:border-b-0">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium">
                  {template.name}{' '}
                  <span className="font-mono text-xs text-slate-600">{template.code}</span>
                </p>
                <p className="mt-1 text-xs text-slate-600">
                  {template.scaleName ?? 'No scale'} ·{' '}
                  {template.sections.length} section(s) ·{' '}
                  {[
                    template.includeSelf && 'self',
                    template.includeManager && 'manager',
                    template.includePeer && 'peer',
                    template.includeUpward && 'upward',
                  ].filter(Boolean).join(', ')} review
                </p>
                {template.weightWarning ? (
                  <p className="mt-1 flex max-w-2xl items-start gap-1 text-xs text-amber-700">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                    {template.weightWarning}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-emerald-700">Weights total 100%.</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge
                  status={template.weightWarning ? 'INCOMPLETE' : 'READY'}
                  tone={template.weightWarning ? 'warning' : 'success'}
                  label={`${template.totalWeight}%`}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setError(null)
                    setSectionDraft({
                      templateId: template.id,
                      sectionOrder: String(template.sections.length + 1),
                      name: '', weightPercent: '', competencyId: '',
                    })
                  }}
                >
                  Add section
                </Button>
                <PermissionGate permission="performance:configure">
                  <Button
                    type="button" variant="ghost"
                    aria-label={`Edit ${template.name}`}
                    onClick={() => {
                      setError(null)
                      setTemplateDraft({
                        id: template.id,
                        scaleId: template.scaleId ?? '',
                        code: template.code,
                        name: template.name,
                        includeSelf: template.includeSelf,
                        includeManager: template.includeManager,
                      })
                    }}
                  >
                    <Pencil className="size-4" aria-hidden="true" />
                    Edit
                  </Button>
                  {template.active && (
                    <Button
                      type="button" variant="outline"
                      onClick={() => {
                        setError(null)
                        setRetiring({ kind: 'template', id: template.id, name: template.name })
                      }}
                    >
                      Retire
                    </Button>
                  )}
                </PermissionGate>
              </div>
            </div>
            {template.sections.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-slate-600">
                {template.sections.map((section) => (
                  <li key={section.id}>
                    {section.sectionOrder}. {section.name} — {section.weightPercent}%
                    {section.competencyName && ` (${section.competencyName})`}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </Card>

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(open) => { if (!open) setRetiring(null) }}
        title={`Retire ${retiring?.name ?? 'this'}?`}
        description="It stops being offered. Everything already rated, scored or run against it keeps naming it — a completed appraisal has to stay readable, so this is never a delete."
        confirmLabel="Retire it"
        loading={retireScale.isPending || retireCompetency.isPending || retireTemplate.isPending}
        error={error}
        onConfirm={() => {
          if (!retiring) return
          setError(null)
          const call = retiring.kind === 'scale' ? retireScale
            : retiring.kind === 'competency' ? retireCompetency : retireTemplate
          call.mutate(retiring.id, {
            onSuccess: () => setRetiring(null),
            onError: (caught) => setError(
              toApiProblem(caught)?.detail ?? 'That could not be retired.'),
          })
        }}
      />

      <FormDialog
        open={scaleDraft != null}
        onOpenChange={(open) => { if (!open) setScaleDraft(null) }}
        title={scaleDraft?.id ? 'Edit rating scale' : 'New rating scale'}
        submitLabel="Save scale"
        loading={saveScale.isPending}
        error={error}
        onSubmit={() => scaleDraft && run(
          () => saveScale.mutateAsync({
            id: scaleDraft.id,
            legalEntityId: entities.data?.[0]?.id ?? null,
            code: scaleDraft.code, name: scaleDraft.name,
          }),
          () => setScaleDraft(null), 'That scale could not be saved')}
      >
        {scaleDraft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="scale-code">Code</Label>
              <Input
                id="scale-code" value={scaleDraft.code}
                onChange={(event) =>
                  setScaleDraft({ ...scaleDraft, code: event.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <Label htmlFor="scale-name">Name</Label>
              <Input
                id="scale-name" value={scaleDraft.name}
                onChange={(event) => setScaleDraft({ ...scaleDraft, name: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={levelDraft != null}
        onOpenChange={(open) => { if (!open) setLevelDraft(null) }}
        title="Add a level"
        description="The number is what people say out loud; the score is what the arithmetic uses."
        submitLabel="Add level"
        loading={addLevel.isPending}
        error={error}
        onSubmit={() => levelDraft && run(
          () => addLevel.mutateAsync({
            id: levelDraft.scaleId,
            body: {
              levelValue: Number(levelDraft.levelValue),
              label: levelDraft.label,
              score: Number(levelDraft.score),
            },
          }),
          () => setLevelDraft(null), 'That level could not be added')}
      >
        {levelDraft && (
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label htmlFor="level-value">Level</Label>
              <Input
                id="level-value" type="number" value={levelDraft.levelValue}
                onChange={(event) =>
                  setLevelDraft({ ...levelDraft, levelValue: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="level-label">Label</Label>
              <Input
                id="level-label" value={levelDraft.label}
                onChange={(event) => setLevelDraft({ ...levelDraft, label: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="level-score">Score</Label>
              <Input
                id="level-score" type="number" value={levelDraft.score}
                onChange={(event) => setLevelDraft({ ...levelDraft, score: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={gapDraft != null}
        onOpenChange={(open) => { if (!open) setGapDraft(null) }}
        title="Gap threshold"
        description="Which ratings start a development conversation."
        submitLabel="Save threshold"
        loading={setGapThreshold.isPending}
        error={error}
        onSubmit={() => gapDraft && run(
          () => setGapThreshold.mutateAsync({
            id: gapDraft.scaleId,
            gapBelowLevel: gapDraft.gapBelowLevel === ''
              ? null : Number(gapDraft.gapBelowLevel),
          }),
          () => setGapDraft(null), 'That threshold could not be saved')}
      >
        {gapDraft && (
          <div>
            <Label htmlFor="gap-level">Ratings below this level become training needs</Label>
            <Input
              id="gap-level" type="number" value={gapDraft.gapBelowLevel}
              onChange={(event) => setGapDraft({ ...gapDraft, gapBelowLevel: event.target.value })}
            />
            <p className="mt-1 text-xs text-slate-600">
              Leave blank for a scale that never creates a training need on its own. That is a
              real choice — it means low ratings are a conversation somebody has by hand.
            </p>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={competencyDraft != null}
        onOpenChange={(open) => { if (!open) setCompetencyDraft(null) }}
        title={competencyDraft?.id ? 'Edit competency' : 'New competency'}
        submitLabel="Save competency"
        loading={saveCompetency.isPending}
        error={error}
        onSubmit={() => competencyDraft && run(
          () => saveCompetency.mutateAsync({
            id: competencyDraft.id,
            legalEntityId: entities.data?.[0]?.id ?? null,
            code: competencyDraft.code,
            name: competencyDraft.name,
            category: competencyDraft.category || null,
          }),
          () => setCompetencyDraft(null), 'That competency could not be saved')}
      >
        {competencyDraft && (
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label htmlFor="comp-code">Code</Label>
              <Input
                id="comp-code" value={competencyDraft.code}
                onChange={(event) => setCompetencyDraft({
                  ...competencyDraft, code: event.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <Label htmlFor="comp-name">Name</Label>
              <Input
                id="comp-name" value={competencyDraft.name}
                onChange={(event) =>
                  setCompetencyDraft({ ...competencyDraft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="comp-category">Category</Label>
              <Input
                id="comp-category" value={competencyDraft.category}
                onChange={(event) =>
                  setCompetencyDraft({ ...competencyDraft, category: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={templateDraft != null}
        onOpenChange={(open) => { if (!open) setTemplateDraft(null) }}
        title={templateDraft?.id ? 'Edit appraisal template' : 'New appraisal template'}
        submitLabel="Save template"
        loading={saveTemplate.isPending}
        error={error}
        onSubmit={() => templateDraft && run(
          () => saveTemplate.mutateAsync({
            id: templateDraft.id,
            legalEntityId: entities.data?.[0]?.id ?? null,
            scaleId: templateDraft.scaleId,
            code: templateDraft.code,
            name: templateDraft.name,
            includeSelf: templateDraft.includeSelf,
            includeManager: templateDraft.includeManager,
          }),
          () => setTemplateDraft(null), 'That template could not be saved')}
      >
        {templateDraft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="tpl-scale">Rating scale</Label>
                <Select
                  id="tpl-scale"
                  className="w-full"
                  value={templateDraft.scaleId}
                  onChange={(event) =>
                    setTemplateDraft({ ...templateDraft, scaleId: event.target.value })}
                >
                  <option value="">Choose a scale…</option>
                  {scales.data?.map((scale) => (
                    <option key={scale.id} value={scale.id}>{scale.name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="tpl-code">Code</Label>
                <Input
                  id="tpl-code" value={templateDraft.code}
                  onChange={(event) => setTemplateDraft({
                    ...templateDraft, code: event.target.value.toUpperCase() })}
                />
              </div>
              <div>
                <Label htmlFor="tpl-name">Name</Label>
                <Input
                  id="tpl-name" value={templateDraft.name}
                  onChange={(event) =>
                    setTemplateDraft({ ...templateDraft, name: event.target.value })}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={templateDraft.includeSelf}
                onChange={(event) =>
                  setTemplateDraft({ ...templateDraft, includeSelf: event.target.checked })}
              />
              Include a self-review
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={templateDraft.includeManager}
                onChange={(event) =>
                  setTemplateDraft({ ...templateDraft, includeManager: event.target.checked })}
              />
              Include a manager review
            </label>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={sectionDraft != null}
        onOpenChange={(open) => { if (!open) setSectionDraft(null) }}
        title="Add a section"
        description="Sections must total 100% before a cycle can open against this template."
        submitLabel="Add section"
        loading={addSection.isPending}
        error={error}
        onSubmit={() => sectionDraft && run(
          () => addSection.mutateAsync({
            id: sectionDraft.templateId,
            body: {
              sectionOrder: Number(sectionDraft.sectionOrder),
              name: sectionDraft.name,
              weightPercent: Number(sectionDraft.weightPercent),
              competencyId: sectionDraft.competencyId || null,
            },
          }),
          () => setSectionDraft(null), 'That section could not be added')}
      >
        {sectionDraft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="sec-order">Position</Label>
              <Input
                id="sec-order" type="number" value={sectionDraft.sectionOrder}
                onChange={(event) =>
                  setSectionDraft({ ...sectionDraft, sectionOrder: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="sec-name">Name</Label>
              <Input
                id="sec-name" value={sectionDraft.name}
                onChange={(event) =>
                  setSectionDraft({ ...sectionDraft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="sec-weight">Weight, %</Label>
              <Input
                id="sec-weight" type="number" value={sectionDraft.weightPercent}
                onChange={(event) =>
                  setSectionDraft({ ...sectionDraft, weightPercent: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="sec-competency">Competency</Label>
              <Select
                id="sec-competency"
                className="w-full"
                value={sectionDraft.competencyId}
                onChange={(event) =>
                  setSectionDraft({ ...sectionDraft, competencyId: event.target.value })}
              >
                <option value="">Not a competency section</option>
                {competencies.data?.map((competency) => (
                  <option key={competency.id} value={competency.id}>{competency.name}</option>
                ))}
              </Select>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
